import { useEffect } from 'react';
import { create } from 'zustand';
import { useMediaQuery, NARROW_QUERY } from '@/shared/hooks/useMediaQuery';

// What occupies the bottom of the screen. A page's BottomBarInfo (a
// document's autosave status or audit line) claims 'info'; a list's fixed
// Pagination claims 'pager'. BottomBar publishes its slot element here so
// BottomBarInfo can portal into it once it exists.
type Kind = 'info' | 'pager';
type State = {
  info: number;
  pager: number;
  slot: HTMLElement | null;
  setSlot: (el: HTMLElement | null) => void;
  claim: (kind: Kind) => () => void;
};

export const useBottomChrome = create<State>((set) => ({
  info: 0,
  pager: 0,
  slot: null,
  setSlot: (el) => set({ slot: el }),
  claim: (kind) => {
    set((s) => ({ [kind]: s[kind] + 1 }) as Partial<State>);
    return () => set((s) => ({ [kind]: Math.max(0, s[kind] - 1) }) as Partial<State>);
  },
}));

export function useBottomChromeClaim(kind: Kind): void {
  useEffect(() => useBottomChrome.getState().claim(kind), [kind]);
}

// D6 (owner decision 2026-10-15): on a phone the 48px bottom band shows only
// when a page puts something in it; otherwise the AI Companion is a 44px round
// button and <main> drops its bottom reserve, unless a fixed pager sits there.
// Desktop: the band as before.
export function useBottomLayout(): { band: boolean; fab: boolean; reserve: boolean } {
  const narrow = useMediaQuery(NARROW_QUERY);
  const info = useBottomChrome((s) => s.info);
  const pager = useBottomChrome((s) => s.pager);
  const band = !narrow || info > 0;
  return { band, fab: !band, reserve: band || pager > 0 };
}
